// src/pages/projects/boqUploadService.js

import {
  collection, getDocs, addDoc,
  query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { reserveItemId } from "../../services/itemIdService";
import { createItem, DUPLICATE_ITEM } from "../../services/itemService";
import { activeCompanyItems } from "../../utils/itemPicker";
import { newBudgetLines } from "../../utils/boqMatch";

// ── FETCH ALL ACTIVE ITEMS FOR COMPANY ────────────────────────────────────────
// P4-3: own + shared "" items; only is_active === false is skipped.
export const fetchActiveItems = async (company_id) => {
  const snap = await getDocs(
    query(collection(db, "items"), where("company_id", "in", [company_id, ""]))
  );
  return activeCompanyItems(snap.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() })), company_id);
};

// ── GENERATE NEXT ITEM-XXXXX ID ───────────────────────────────────────────────
// D-300: platform-wide id from the shared counter (itemIdService.js).
// The old per-company "max + 1" gave every company its own ITEM-00001.
// Call this ONCE PER NEW ITEM — never add 1 to its result yourself.
export const generateNextItemId = async () => reserveItemId();

// ── CREATE NEW ITEM + INVENTORY SUMMARY ───────────────────────────────────────
// Returns the created item object with item_id and item_code.
// P4-2: written by itemService.createItem() (code = MT- + id number, items +
// stock doc in ONE batch, name-key duplicate check). A name that already exists
// as exactly ONE active item links to that item (reused: true — Ravi, 30 Oct);
// two or more → stops with the codes (P4-3: the preview makes the user pick
// first, so this only happens if Item Master changed after the preview).
// NOTE: item_id is generated fresh per call; caller must pass unique item_ids
// when creating multiple items in one session (use generateNextItemId serially).
// specification (optional, 14 Sep 2026): keeps item_name short/scannable
// for dropdowns while the full technical detail (thickness, insulation,
// construction, standards) lives here instead — mirrors the split already
// applied in ItemMaster.js's manual "Add New Item" form and parseBoqWithAI.js's
// item_name/description extraction fields, so a new item created via
// AI BOQ Import or manual BOQ upload gets the same clean short-name
// treatment as one added by hand. Optional and backward-compatible —
// omitting it (existing callers) just leaves the field blank, same as
// any pre-14-Sep item.
export const createNewItem = async (company_id, item_id, name, unit, specification = "") => {
  try {
    const created = await createItem({
      company_id, item_id, item_name: name, unit, category: "MATERIAL",
      specification, source: "boq_upload",
    });
    return {
      item_id:       created.item_id,
      item_code:     created.item_code,
      item_name:     created.item_name,
      specification: created.specification,
      unit:          created.unit,
    };
  } catch (err) {
    if (err?.code === DUPLICATE_ITEM && err.existing?.length === 1) {
      const e = err.existing[0];
      return {
        item_id:       e.item_id,
        item_code:     e.item_code     || "",
        item_name:     e.item_name,
        specification: e.specification || "",
        unit:          e.unit          || unit,
        reused:        true,
      };
    }
    if (err?.code === DUPLICATE_ITEM) {
      throw new Error(`${err.message.replace(/ Use the existing item instead\.$/, "")} — pick one.`);
    }
    throw err;
  }
};

// ── FETCH EXISTING STAGES FOR PROJECT ─────────────────────────────────────────
export const fetchExistingStageNames = async (company_id, project_id) => {
  const snap = await getDocs(
    query(
      collection(db, "project_material_stages"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id)
    )
  );
  const map = {};
  snap.docs.forEach(d => {
    const data = d.data();
    map[(data.stage_name || "").toLowerCase().trim()] = { id: d.id, ...data };
  });
  return map; // key: stage_name.toLowerCase() → stage doc
};

// ── GET NEXT STAGE NO FOR PROJECT ─────────────────────────────────────────────
const getNextStageNo = async (company_id, project_id) => {
  const snap = await getDocs(
    query(
      collection(db, "project_material_stages"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id)
    )
  );
  return snap.size + 1;
};

// ── BUILD BUDGET ITEM FROM PARSED ROW + RESOLVED ITEM ─────────────────────────
const buildBudgetItem = (row, resolvedItem) => {
  const side       = row.side_normalized; // "high_side" | "low_side"
  const buffer_pct = side === "low_side" ? 15 : 0;
  const qty_raw    = Number(row.qty || 0);
  const qty_final  = side === "low_side"
    ? Math.ceil(qty_raw * (1 + buffer_pct / 100))
    : qty_raw;
  const cost_rate  = Number(row.rate || 0);

  return {
    item_id:       resolvedItem.item_id   || "",
    item_code:     resolvedItem.item_code || "",
    item_name:     resolvedItem.item_name || row.item_name,
    unit:          resolvedItem.unit      || row.unit || "Nos",
    side,
    buffer_pct,
    qty_entered:   qty_raw,
    qty_budgeted:  qty_final,
    qty_issued:    0,
    qty_remaining: qty_final,
    cost_rate,
    budget_amount: qty_final * cost_rate,
    issued_amount: 0,
    alert_level:   "ok",
    budget_pct:    0,
    override_log:  [],
  };
};

// ── SAVE BOQ STAGES ───────────────────────────────────────────────────────────
// stages: array of { stage_name, rows: [{...parsed row with resolvedItem attached}] }
// replaceExisting: Set of stage_name (lowercase) to replace instead of merge
export const saveBOQStages = async ({
  company_id,
  project_id,
  project_name,
  stages,
  replaceExisting,
  existingStageMap,
  user,
}) => {
  let stagesCreated = 0;
  let itemsImported = 0;

  // P4-3 (decision 2): no budget line without an item_id, checked before any write.
  const noItem = stages.flatMap(s => s.rows).filter(r => !String(r.resolvedItem?.item_id || "").trim());
  if (noItem.length > 0) {
    throw new Error(`Not saved. These rows have no Item Master item: ${noItem.map(r => r.item_name).join(", ")}`);
  }

  for (const stage of stages) {
    const nameKey        = (stage.stage_name || "").toLowerCase().trim();
    const existingStage  = existingStageMap[nameKey];
    const isReplace      = replaceExisting.has(nameKey);

    const budgetItems = stage.rows.map(row => buildBudgetItem(row, row.resolvedItem));
    itemsImported += budgetItems.length;

    if (existingStage && !isReplace) {
      // Merge — append items not already in the stage (by item_id: two
      // same-name items are two items)
      const newItems = newBudgetLines(existingStage.material_budget, budgetItems);
      if (newItems.length > 0) {
        const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
        await updateDoc(firestoreDoc(db, "project_material_stages", existingStage.id), {
          material_budget: [...(existingStage.material_budget || []), ...newItems],
          updated_at:      serverTimestamp(),
        });
      }
    } else if (existingStage && isReplace) {
      // Replace — overwrite material_budget
      const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
      await updateDoc(firestoreDoc(db, "project_material_stages", existingStage.id), {
        material_budget: budgetItems,
        updated_at:      serverTimestamp(),
      });
    } else {
      // Create new stage
      const stage_no = await getNextStageNo(company_id, project_id);
      await addDoc(collection(db, "project_material_stages"), {
        company_id,
        project_id,
        project_name,
        stage_no,
        stage_name:      stage.stage_name,
        trigger_items:   [],
        status:          "locked",
        material_budget: budgetItems,
        created_by:      user.uid,
        created_by_name: user.name || user.email || "",
        created_at:      serverTimestamp(),
        updated_at:      serverTimestamp(),
      });
      stagesCreated++;
    }
  }

  return { stagesCreated, itemsImported };
};
